/**
 * @author      david.loehr@zumper.com (David Loehr)
 * @copyright   Copyright (c) 2023, Zumper
 */

import { Button, Checkbox } from '@chakra-ui/react'
import { Close } from '@zcode-ui/icons'
import React, { type MouseEventHandler } from 'react'
import invariant from 'tiny-invariant'

export type ListingCardCheckboxOrCloseButtonProps =
  | {
      isChecked: boolean
      onClick: (
        event: React.MouseEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>
      ) => void
      onClose?: undefined
    }
  | {
      isChecked?: undefined
      onClick?: React.MouseEventHandler<HTMLDivElement> | undefined
      onClose: MouseEventHandler<HTMLButtonElement> | undefined
    }
  | {
      isChecked?: undefined
      onClick?: React.MouseEventHandler<HTMLDivElement> | undefined
      onClose?: undefined
    }

const position = {
  left: '14px',
  position: 'absolute',
  top: '14px',
} as const

const HALF_BLACK = 'rgba(0, 0, 0, 0.5)'

export const ListingCardCheckboxOrCloseButton = ({
  isChecked,
  onClick,
  onClose,
}: ListingCardCheckboxOrCloseButtonProps) => {
  if (onClose) {
    invariant(
      typeof isChecked === 'undefined',
      'at least one of onClose or isChecked must be undefined'
    )
    return (
      <Button
        {...position}
        _hover={{
          background: HALF_BLACK,
        }}
        aria-label="close"
        background={HALF_BLACK}
        borderRadius="full"
        height={6}
        minWidth={6}
        onClick={onClose}
        padding={0}
        width={6}
      >
        <Close fill="var(--chakra-colors-gray-0)" size={10} strokeWidth={2} />
      </Button>
    )
  }
  if (typeof isChecked === 'boolean') {
    return (
      <Checkbox
        {...position}
        boxShadow="0px 2px 4px 0px rgba(0, 0, 0, 0.06)"
        isChecked={isChecked}
        onChange={onClick}
        sx={{
          '> :not(:checked) + span': {
            background: 'gray.0',
            borderColor: 'gray.250',
          },
        }}
      />
    )
  }
  return null
}
